import { Box, Stack, Text } from '@chakra-ui/react'
import debounce from 'lodash/debounce'
import type { FC, FormEventHandler, KeyboardEventHandler, ClipboardEventHandler } from 'react'
import { useMemo } from 'react'
import React, { useState, useRef, useEffect, useCallback, forwardRef } from 'react'
import ContentEditable from 'react-contenteditable'

import { MenuRoot, MenuItem } from '@app/components/ui/menu'
import useGetObjectsByProperties from '@app/hooks/useGetObjectsByProperties'
import { getObjectsByProperties, useStore } from '@app/store'
import type { BoundedState } from '@app/store/types'
import type { DomainMetric } from '@app/types'
import { Metric, MetricsAutocomplete } from '@graphql/documents/metric.graphql'
import type {
  MetricQuery,
  MetricQueryVariables,
  MetricsAutocompleteQuery,
  MetricsAutocompleteQueryVariables
} from '@graphql/queries'
import type { MetricSource } from '@graphql/types'

// Function to convert HTML formula to text formula with metric IDs
function convertHtmlToTextFormula(html: string): string {
  // Create a temporary div to parse the HTML
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = html

  // Process all metric tag wrappers
  const metricTagWrappers = tempDiv.querySelectorAll('.metric-tag-wrapper')
  metricTagWrappers.forEach((wrapper) => {
    const slug = wrapper.getAttribute('data-slug')
    if (slug) {
      // Replace the wrapper with the metric slug
      wrapper.replaceWith(slug)
    }
  })

  return tempDiv.textContent || ''
}

type Suggestion = Pick<DomainMetric, 'id' | 'name' | 'slug'>

function createTag(suggestion: Suggestion) {
  // Create the metric tag
  const metricTag = document.createElement('span')
  metricTag.contentEditable = 'false'
  metricTag.style.backgroundColor = 'var(--chakra-colors-bg-emphasized)'
  metricTag.style.borderRadius = '3px'
  metricTag.style.padding = '0 4px'
  metricTag.style.margin = '0 2px'
  metricTag.textContent = suggestion.name

  // Create a wrapper with tooltip functionality
  const wrapper = document.createElement('div')
  wrapper.classList.add('metric-tag-wrapper')
  wrapper.setAttribute('data-id', suggestion.id)
  wrapper.setAttribute('data-slug', suggestion.slug)
  wrapper.style.position = 'relative'
  wrapper.style.display = 'inline-block'
  wrapper.contentEditable = 'false'

  // Create a tooltip element
  const tooltip = document.createElement('span')
  tooltip.classList.add('metric-tag-tooltip')
  tooltip.textContent = suggestion.slug
  tooltip.style.position = 'absolute'
  tooltip.style.backgroundColor = 'var(--chakra-colors-gray-700)'
  tooltip.style.color = 'white'
  tooltip.style.padding = '2px 6px'
  tooltip.style.borderRadius = '4px'
  tooltip.style.fontSize = '12px'
  tooltip.style.bottom = 'calc(100% + 5px)'
  tooltip.style.left = '50%'
  tooltip.style.transform = 'translateX(-50%)'
  tooltip.style.whiteSpace = 'nowrap'
  tooltip.style.opacity = '0'
  tooltip.style.visibility = 'hidden'
  tooltip.style.transition = 'opacity 0.2s, visibility 0.2s'
  tooltip.style.zIndex = '1000'

  // Add a small arrow pointing down
  const arrow = document.createElement('span')
  arrow.style.position = 'absolute'
  arrow.style.bottom = '-4px'
  arrow.style.left = '50%'
  arrow.style.transform = 'translateX(-50%) rotate(45deg)'
  arrow.style.width = '8px'
  arrow.style.height = '8px'
  arrow.style.backgroundColor = 'var(--chakra-colors-gray-700)'
  tooltip.appendChild(arrow)

  // NOTE: the standard mouseenter / mouseleave events don't trigger with contentEditable elements, forcing us to get creative.
  if (!document.getElementById('formula-editor-metric-tag-tooltip-style')) {
    const style = document.createElement('style')
    style.id = 'formula-editor-metric-tag-tooltip-style'
    style.textContent = `
      .metric-tag-wrapper:hover .metric-tag-tooltip {
        opacity: 1 !important;
        visibility: visible !important;
      }
    `
    document.head.appendChild(style)
  }

  // Append the metric tag and tooltip to the wrapper
  wrapper.appendChild(metricTag)
  wrapper.appendChild(tooltip)

  return wrapper
}

function createLoadingIndicator(pastedText: string) {
  // Create and insert a loading indicator while we wait for the query
  const loadingElement = document.createElement('span')
  loadingElement.classList.add('metric-placeholder')
  loadingElement.setAttribute('data-text', pastedText)
  loadingElement.contentEditable = 'false'
  loadingElement.style.backgroundColor = 'var(--chakra-colors-bg-muted)'
  loadingElement.style.borderRadius = '3px'
  loadingElement.style.padding = '0 4px'
  loadingElement.style.margin = '0 2px'
  loadingElement.style.opacity = '0.7'
  loadingElement.textContent = `Loading ${pastedText}...`
  return loadingElement
}

const queryMetric = async (metricSlug: string, loaderQuery: BoundedState['loaderQuery']) => {
  const store = useStore.getState()
  const existingMetric = getObjectsByProperties(store, 'metric', { slug: metricSlug })?.[0]

  if (existingMetric) {
    return existingMetric
  }

  const result = await loaderQuery<MetricQuery, MetricQueryVariables>(Metric, {
    id: metricSlug
  })

  return result?.data?.metric
}

type QuerySuggestions = (
  filter: string,
  strategyId: string | null,
  loaderQuery: BoundedState['loaderQuery']
) => Promise<Suggestion[]>
const querySuggestions = debounce<QuerySuggestions>(async (filter, strategyId, loaderQuery) => {
  const result = await loaderQuery<MetricsAutocompleteQuery, MetricsAutocompleteQueryVariables>(MetricsAutocomplete, {
    query: filter,
    strategyId
  })

  return result?.data?.metricsAutocomplete
}, 150)

function toHTMLString(structuredFormula: MetricSource['structuredFormula']) {
  return () => {
    if (!structuredFormula || structuredFormula.length === 0) {
      return ''
    }

    const tempDiv = document.createElement('div')

    structuredFormula.forEach((item) => {
      if (item.__typename === 'FormulaText') {
        const textNode = document.createTextNode(item.text)
        tempDiv.appendChild(textNode)
      } else {
        // It's a Metric object
        const metricTag = createTag(item)
        tempDiv.appendChild(metricTag)
        // Add a space after each metric tag
        tempDiv.appendChild(document.createTextNode(' '))
      }
    })

    return tempDiv.innerHTML
  }
}

type SuggestionDropdownProps = {
  suggestions: Suggestion[]
  suggestionFilter: string
  handleSelectSuggestion: (suggestion: Suggestion) => void
  strategyId?: string
  mapMetricIds: string[]
}

const SuggestionDropdown = forwardRef<HTMLDivElement, SuggestionDropdownProps>(
  ({ suggestions, suggestionFilter, handleSelectSuggestion, strategyId, mapMetricIds }, ref) => (
    <Box
      ref={ref}
      position="absolute"
      top="calc(100% + 5px)"
      left="0"
      zIndex="dropdown"
      width="250px"
      maxHeight="200px"
      overflowY="auto"
      overflowX="hidden"
      bg="bg.subtle"
      boxShadow="md"
      borderRadius="md"
      border="1px solid"
      borderColor="border.200"
    >
      {suggestions.length > 1 ? (
        <MenuRoot>
          {suggestions.map((suggestion) => {
            let maybeOnMap

            if (strategyId) {
              if (mapMetricIds.includes(suggestion.id)) {
                maybeOnMap = (
                  <Text fontSize="2xs" color="fg.500">
                    On this map
                  </Text>
                )
              } else {
                maybeOnMap = (
                  <Text fontSize="2xs" color="fg.500">
                    Not on map
                  </Text>
                )
              }
            }

            return (
              <MenuItem
                key={suggestion.id}
                px="3"
                py="2"
                cursor="pointer"
                _hover={{ bg: 'bg.emphasized' }}
                value={suggestion.id}
                onClick={() => handleSelectSuggestion(suggestion)}
              >
                <Stack gap={0}>
                  <Text fontWeight="medium">{suggestion.name}</Text>
                  {maybeOnMap}
                  <Text fontSize="2xs" color="fg.subtle">
                    {suggestion.slug}
                  </Text>
                </Stack>
              </MenuItem>
            )
          })}
        </MenuRoot>
      ) : (
        <Box p="3">
          {suggestionFilter && suggestionFilter.length > 3 ? (
            <Text color="fg.500">No suggestions found</Text>
          ) : (
            <Text fontSize="xs" color="fg.500">
              Continue typing to filter results...
            </Text>
          )}
        </Box>
      )}
    </Box>
  )
)

type Props = {
  defaultValue?: MetricSource['structuredFormula']
  name: string
  placeholder?: string
  disabled?: boolean
  strategyId?: string
  metricId?: string
}

const FormulaEditor: FC<Props> = ({
  defaultValue = [],
  name,
  placeholder = '',
  disabled = false,
  strategyId,
  metricId
}) => {
  const [html, setHtml] = useState<string>(toHTMLString(defaultValue))
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [suggestionFilter, setSuggestionFilter] = useState('')
  const contentEditableRef = useRef<HTMLElement>(null)
  const hiddenInputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const loaderQuery = useStore.use.loaderQuery()
  const metricNodes = useGetObjectsByProperties('node', {
    strategyId,
    type: 'metric'
  })
  const mapMetricIds = useMemo(() => metricNodes.map((m) => m.domainObjectId), [metricNodes])

  useEffect(() => {
    // Fetch suggestions when the component mounts or suggestionFilter changes
    const fetchSuggestions = async () => {
      const fetchedSuggestions = await querySuggestions(suggestionFilter, strategyId, loaderQuery)
      if (fetchedSuggestions) {
        setSuggestions(fetchedSuggestions.filter((s) => s.id !== metricId)) // Exclude the current metric
      }
    }

    fetchSuggestions()
  }, [suggestionFilter, strategyId, metricId, loaderQuery])

  // Get text before cursor
  const getTextBeforeCursor = useCallback(() => {
    try {
      const selection = window.getSelection()
      if (!selection || selection.rangeCount === 0) {
        return ''
      }

      const range = selection.getRangeAt(0).cloneRange()

      // Make sure we have a valid reference and the node is in the document
      if (contentEditableRef.current && document.contains(contentEditableRef.current)) {
        range.setStart(contentEditableRef.current, 0)
      } else {
        // Fallback to document.body only if it's available
        if (document.body) {
          range.setStart(document.body, 0)
        } else {
          return ''
        }
      }

      return range.toString()
    } catch (_error) {
      // Silently handle the error and return empty string
      return ''
    }
  }, [contentEditableRef])

  // Handle content changes
  const handleChange: FormEventHandler<HTMLFormElement> = useCallback((evt) => {
    const newValue = evt.currentTarget.innerHTML
    setHtml(newValue)

    // Update the hidden input value with text formula
    if (hiddenInputRef.current) {
      hiddenInputRef.current.value = convertHtmlToTextFormula(newValue)
    }
  }, [])

  // Track cursor position and check for @ symbol
  const handleKeyUp: KeyboardEventHandler<HTMLElement> = useCallback(
    (_e) => {
      try {
        const selection = window.getSelection()
        if (selection && selection.rangeCount > 0) {
          // Get text before cursor
          const textBeforeCursor = getTextBeforeCursor()

          // Only proceed if we have valid text
          if (textBeforeCursor === null || textBeforeCursor === undefined) {
            return
          }

          // Check if @ was just typed
          if (textBeforeCursor.endsWith('@')) {
            setShowSuggestions(true)
            setSuggestionFilter('')
          } else if (textBeforeCursor.includes('@')) {
            // Check if we're in a filtering state (after @ but before space)
            const lastAtIndex = textBeforeCursor.lastIndexOf('@')
            if (lastAtIndex === -1) {
              setShowSuggestions(false)
              return
            }

            const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1)

            // If there's a space after @, close suggestions
            if (textAfterAt.includes(' ') || textAfterAt.includes('\n')) {
              setShowSuggestions(false)
            } else {
              setShowSuggestions(true)
              setSuggestionFilter(textAfterAt)
            }
          } else {
            setShowSuggestions(false)
          }
        }
      } catch (_error) {
        // If any error occurs, ensure suggestions are hidden to prevent UI issues
        setShowSuggestions(false)
      }
    },
    [getTextBeforeCursor]
  )

  // Handle special keys like backspace and delete
  const handleKeyDown: KeyboardEventHandler<HTMLElement> = useCallback(
    (e) => {
      try {
        if ((e.key === 'Backspace' || e.key === 'Delete') && contentEditableRef.current) {
          const selection = window.getSelection()
          if (selection && selection.rangeCount > 0) {
            try {
              const range = selection.getRangeAt(0)

              // Check if cursor is at the end of a metric tag
              if (range.startOffset === 0 && range.collapsed) {
                const node = range.startContainer
                if (node && node.parentElement && node.parentElement.classList.contains('metric-tag-wrapper')) {
                  // Remove the entire tag
                  node.parentElement.remove()
                  e.preventDefault()
                }
              }
            } catch (_error) {
              // If there's an error accessing the range, just continue
            }
          }
        }

        // Close suggestions on Escape
        if (e.key === 'Escape') {
          setShowSuggestions(false)
        }
      } catch (_error) {
        // If any error occurs, ensure suggestions are hidden to prevent UI issues
        setShowSuggestions(false)
      }
    },
    [contentEditableRef]
  )

  // Handle suggestion selection
  const handleSelectSuggestion = useCallback(
    (suggestion: Suggestion) => {
      try {
        if (!contentEditableRef.current) {
          return
        }

        const selection = window.getSelection()
        if (!selection || selection.rangeCount === 0) {
          return
        }

        // Find the position of the last @ symbol before cursor
        const textBeforeCursor = getTextBeforeCursor()
        if (!textBeforeCursor) {
          setShowSuggestions(false)
          return
        }

        const lastAtIndex = textBeforeCursor.lastIndexOf('@')
        if (lastAtIndex === -1) {
          setShowSuggestions(false)
          return
        }

        // Create a range from the @ to the cursor
        const atRange = document.createRange()
        let charCount = 0
        let foundStart = false
        let rangeSet = false

        // Traverse the DOM to find the position of the @ character
        const findAtPosition = (node: Node): boolean => {
          try {
            if (!node) {
              return false
            }

            if (node.nodeType === Node.TEXT_NODE) {
              const text = node.textContent || ''
              for (let i = 0; i < text.length; i++) {
                charCount++
                if (charCount === lastAtIndex + 1) {
                  try {
                    atRange.setStart(node, i)
                    foundStart = true
                  } catch (_error) {
                    // If setting start fails, abort
                    return false
                  }
                }
                if (foundStart && charCount === textBeforeCursor.length) {
                  try {
                    atRange.setEnd(node, i + 1)
                    rangeSet = true
                    return true
                  } catch (_error) {
                    // If setting end fails, abort
                    return false
                  }
                }
              }
            } else {
              for (let i = 0; i < node.childNodes.length; i++) {
                if (findAtPosition(node.childNodes[i])) {
                  return true
                }
              }
            }
            return false
          } catch (_error) {
            return false
          }
        }

        if (contentEditableRef.current) {
          findAtPosition(contentEditableRef.current)
        }

        // Only proceed if we successfully set the range
        if (!rangeSet) {
          setShowSuggestions(false)
          return
        }

        // Create the metric tag
        const metricTag = createTag(suggestion)

        // Replace the @ and any text after it with the metric tag
        selection.removeAllRanges()
        selection.addRange(atRange)

        // Delete the selected text
        atRange.deleteContents()

        // Insert the metric tag
        const spaceTextNode = document.createTextNode(' ')

        // Insert the elements
        atRange.insertNode(spaceTextNode)
        atRange.insertNode(metricTag)

        // Move cursor after the inserted content
        const newRange = document.createRange()
        newRange.setStartAfter(spaceTextNode)
        newRange.collapse(true)
        selection.removeAllRanges()
        selection.addRange(newRange)

        // Update the HTML state to reflect the changes
        if (contentEditableRef.current) {
          setHtml(contentEditableRef.current.innerHTML)
        }

        setSuggestions([]) // clear the previous results
        setShowSuggestions(false)
      } catch (_error) {
        // If any error occurs, ensure suggestions are hidden to prevent UI issues
        setShowSuggestions(false)
      }
    },
    [getTextBeforeCursor]
  )

  // Handle paste events
  const handlePaste: ClipboardEventHandler<HTMLElement> = useCallback(
    async (e) => {
      try {
        // Get the pasted text
        const pastedText = e.clipboardData.getData('text/plain').trim()

        // Check if the pasted text starts with a letter and contains no spaces
        if (pastedText && /^[a-zA-Z]/.test(pastedText) && !pastedText.includes(' ')) {
          // If we found a metric, create a tag for it
          e.preventDefault() // Prevent default paste behavior - has to be done before the async call.

          // Query the metric using the pasted text as metricId
          const selection = window.getSelection()
          if (!selection || selection.rangeCount === 0) {
            return
          }

          // Get the current selection range
          const range = selection.getRangeAt(0)
          const loadingElement = createLoadingIndicator(pastedText)

          range.deleteContents()
          range.insertNode(loadingElement)

          // Update the HTML state to reflect the loading indicator
          if (contentEditableRef.current) {
            setHtml(contentEditableRef.current.innerHTML)
          }

          // Query for the metric
          const metric = await queryMetric(pastedText, loaderQuery)
          let pasteEnd

          // Create a new range that targets the loading indicator
          const newRange = document.createRange()
          newRange.selectNode(loadingElement)

          if (metric) {
            const suggestion: Suggestion = {
              id: metric.id,
              name: metric.name,
              slug: metric.slug
            }

            // Create the metric tag
            const metricTag = createTag(suggestion)

            // Replace the loading indicator with the metric tag
            newRange.deleteContents()
            newRange.insertNode(metricTag)

            // Add a space after the tag
            const spaceTextNode = document.createTextNode(' ')
            newRange.setStartAfter(metricTag)
            newRange.insertNode(spaceTextNode)

            pasteEnd = spaceTextNode
          } else {
            // If no metric found, replace the loading indicator with the pasted text
            const textNode = document.createTextNode(pastedText)

            newRange.deleteContents()
            newRange.insertNode(textNode)

            pasteEnd = textNode
          }

          // Move cursor after the inserted content
          const cursorRange = document.createRange()
          cursorRange.setStartAfter(pasteEnd)
          cursorRange.collapse(true)
          selection.removeAllRanges()
          selection.addRange(cursorRange)

          // Update the HTML state to reflect the changes
          if (contentEditableRef.current) {
            setHtml(contentEditableRef.current.innerHTML)
          }
        }
      } catch (_error) {
        // If any error occurs, give up
      }
    },
    [loaderQuery]
  )

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowSuggestions(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <Box position="relative" w="full">
      <ContentEditable
        innerRef={contentEditableRef}
        html={html}
        disabled={disabled}
        onChange={handleChange}
        onKeyUp={handleKeyUp}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        style={{
          width: '100%',
          minHeight: '80px',
          padding: '8px 12px',
          border: '1px solid',
          borderColor: 'inherit',
          borderRadius: 'md',
          backgroundColor: disabled ? 'var(--chakra-colors-bg-muted)' : 'var(--chakra-colors-bg)'
        }}
        placeholder={placeholder}
      />

      <input type="hidden" name={name} ref={hiddenInputRef} value={convertHtmlToTextFormula(html)} readOnly />

      {showSuggestions && (
        <SuggestionDropdown
          ref={dropdownRef}
          suggestions={suggestions}
          strategyId={strategyId}
          mapMetricIds={mapMetricIds}
          suggestionFilter={suggestionFilter}
          handleSelectSuggestion={handleSelectSuggestion}
        />
      )}
    </Box>
  )
}

export default FormulaEditor
